import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { formatIstDateTimeFromParts, formatIstDateKey } from './ist-datetime';
import './TradingLedger.css';

const LEDGER_CONFIG = {
  normal: {
    apiPath: '/api/normal/ledger',
    title: 'Normal trading ledger',
    description: 'Bot trades only — BUY/SELL paper trades from the bot',
    poolLabel: 'Pool used',
    counts: (c) => `${c?.bot ?? 0} bot · ${c?.v1Sold ?? 0} V1 sells · ${c?.total ?? 0} total`,
    themeClass: 'tl-normal',
    emptyLabel: 'normal stock'
  },
  options: {
    apiPath: '/api/options/ledger',
    title: 'Options trading ledger',
    description: 'NIFTY options bot trades and alerts — ₹100/day cap only',
    poolLabel: 'Day cap',
    counts: (c) => `${c?.bot ?? 0} options trades · ${c?.activity ?? 0} alerts`,
    themeClass: 'tl-options',
    emptyLabel: 'options'
  },
  cnc: {
    apiPath: '/api/cnc-degen/ledger',
    title: 'CNC Delivery ledger',
    description: 'CNC delivery paper trades — BUY/SELL from the CNC Delivery bot',
    poolLabel: 'Capital in use',
    counts: (c) => `${c?.bot ?? 0} CNC trades · ${c?.total ?? 0} total`,
    themeClass: 'tl-cnc',
    emptyLabel: 'CNC delivery'
  }
};

function fmtRs(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const sign = v < 0 ? '−' : '';
  return `${sign}₹${Math.abs(v).toFixed(2)}`;
}

function actionClass(action) {
  const a = String(action || '').toUpperCase();
  if (['BUY', 'LONG', 'CNC_BUY'].includes(a)) return 'buy';
  if (['SELL', 'COVER', 'SHORT', 'CNC_SELL'].includes(a)) return 'sell';
  return 'info';
}

export default function TradingLedger({
  defaultDate,
  fullPage = false,
  ledgerType = 'normal'
}) {
  const config = LEDGER_CONFIG[ledgerType] || LEDGER_CONFIG.normal;
  const today = defaultDate || new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(today);
  const [dates, setDates] = useState([today]);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadLedger = useCallback(async (date) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(config.apiPath, { params: { date } });
      if (res.data?.ledgerType && res.data.ledgerType !== ledgerType) {
        throw new Error('Wrong ledger type returned from server');
      }
      setLedger(res.data);
      if (res.data.dates?.length) setDates(res.data.dates);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not load ledger');
      setLedger(null);
    } finally {
      setLoading(false);
    }
  }, [config.apiPath, ledgerType]);

  useEffect(() => {
    if (defaultDate) setSelectedDate(defaultDate);
  }, [defaultDate]);

  useEffect(() => {
    loadLedger(selectedDate);
  }, [selectedDate, loadLedger]);

  const j = ledger?.journal;
  const entries = ledger?.entries || [];

  return (
    <section
      className={`tl-ledger ${config.themeClass}${fullPage ? ' tl-full' : ''}`}
      aria-label={config.title}
    >
      <header className="tl-head">
        <div>
          {!fullPage && <h2>{config.title}</h2>}
          <p>{config.description}</p>
        </div>
        <div className="tl-date-pick">
          <label htmlFor={`tl-date-input-${ledgerType}`}>Select date &amp; time</label>
          <input
            id={`tl-date-input-${ledgerType}`}
            type="date"
            value={selectedDate}
            max={today}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <select
            aria-label="Quick pick date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            {dates.map((d) => (
              <option key={d} value={d}>{formatIstDateKey(d)}</option>
            ))}
          </select>
        </div>
      </header>

      {loading && <p className="tl-status">Loading ledger…</p>}
      {error && <p className="tl-error">{error}</p>}

      {!loading && j && (
        <div className="tl-summary">
          <div className="tl-sum-card">
            <span>Day P&amp;L</span>
            <strong className={j.netPnl >= 0 ? 'up' : 'down'}>{fmtRs(j.netPnl)}</strong>
          </div>
          <div className="tl-sum-card">
            <span>{j.profitTarget != null ? `Target ₹${j.profitTarget}` : 'Mode'}</span>
            <strong className={j.targetHit ? 'up' : ''}>
              {j.profitTarget != null ? (j.targetHit ? 'Hit ✓' : 'Not hit') : 'Earn more'}
            </strong>
          </div>
          <div className="tl-sum-card">
            <span>Trades</span>
            <strong>{j.tradesCount ?? ledger?.counts?.total ?? 0}</strong>
          </div>
          <div className="tl-sum-card">
            <span>Invested</span>
            <strong>{fmtRs(j.totalInvested)}</strong>
          </div>
          <div className="tl-sum-card">
            <span>Returned</span>
            <strong>{fmtRs(j.totalReturned)}</strong>
          </div>
          <div className="tl-sum-card">
            <span>{config.poolLabel}</span>
            <strong>{fmtRs(j.capitalInUse ?? j.totalInvested)}</strong>
          </div>
        </div>
      )}

      {!loading && ledger && (
        <p className="tl-counts">{config.counts(ledger.counts)}</p>
      )}

      {!loading && entries.length > 0 && (
        <div className="tl-table-wrap">
          <table className="tl-table">
            <thead>
              <tr>
                <th>Date &amp; time</th>
                <th>Source</th>
                <th>Action</th>
                <th>{ledgerType === 'options' ? 'Contract' : 'Stock'}</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Amount</th>
                <th>P&amp;L</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => (
                <tr key={row.id}>
                  <td>{row.dateTime || formatIstDateTimeFromParts(selectedDate, row.time)}</td>
                  <td><em className={`tl-src ${row.source.toLowerCase()}`}>{row.source}</em></td>
                  <td><span className={`tl-act ${actionClass(row.action)}`}>{row.action}</span></td>
                  <td><strong>{row.stock}</strong></td>
                  <td>{row.qty ?? '—'}</td>
                  <td>{row.price != null ? `₹${Number(row.price).toFixed(2)}` : '—'}</td>
                  <td>{row.amount != null ? fmtRs(row.amount) : '—'}</td>
                  <td className={row.pnl > 0 ? 'up' : row.pnl < 0 ? 'down' : ''}>
                    {row.pnl != null ? fmtRs(row.pnl) : '—'}
                  </td>
                  <td className="tl-note">{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <p className="tl-empty">No {config.emptyLabel || ledgerType} ledger entries for {formatIstDateKey(selectedDate)}</p>
      )}
    </section>
  );
}
